import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import Calendar from '../../components/Calendar';
import './Calendar.css';

const StudentCalendar = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getStudentEnrollments, data } = useData();

  const userId = user?.id || user?._id;
  const enrollments = getStudentEnrollments(userId);
  const enrolledCourseIds = new Set(enrollments.map(e => String(e.courseId || e.course?.id)));

  const studentCohortId = String(user?.cohort?.id || user?.cohort?._id || user?.cohort || '');
  const seenEventKeys = new Set();
  
  const events = (data.schedules || [])
    .filter((event) => {
      const eventCohortId = String(event.cohortId || event.cohort?.id || event.cohort?._id || event.cohort || '');
      if (studentCohortId && eventCohortId && eventCohortId !== studentCohortId) {
        return false;
      }
      return enrolledCourseIds.has(String(event.courseId));
    })
    .filter((event) => {
      const key = [
        String(event.courseId || ''),
        String(event.start || event.startDateTime || ''),
        String(event.end || event.endDateTime || ''),
        String(event.eventType || event.type || ''),
        String(event.title || '')
      ].join('|');
      if (seenEventKeys.has(key)) {
        return false;
      }
      seenEventKeys.add(key);
      return true;
    })
    .map(event => ({
      ...event,
      backgroundColor: event.cohortColor || '#6D28D9',
      borderColor: event.cohortColor || '#6D28D9',
      title: (event.courseCode && !String(event.title || '').startsWith(`${event.courseCode} - `))
        ? `${event.courseCode} - ${event.title}`
        : event.title,
      extendedProps: {
        // We verspreiden het hele event zodat álle originele databasevelden (raw data) bewaard blijven
        ...event, 
        courseName: event.courseName,
        courseCode: event.courseCode,
        location: event.location,
        cohortName: event.cohortName || '',
        cohortColor: event.cohortColor || '#6D28D9',
        eventType: event.eventType || event.type || 'college',
        type: event.type || event.eventType || 'college',
        examType: event.examType || null,
        mode: event.mode || 'onsite',
        meetingUrl: event.meetingUrl || '',
        description: event.description || ''
      }
    }));

  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
  };

  return (
    <>
      <div className="student-calendar-page">
        <div className="page-header">
          <h1>{t('student.myCalendar')}</h1>
        </div>

        <Calendar 
          events={events}
          onEventClick={handleEventClick}
          editable={false}
        />
      </div>

      {selectedEvent && (
        <div className="event-details">
          {(() => {
            const props = selectedEvent.extendedProps || {};
            // Match op ID, of val terug op de meegegeven props (bevat nu het volledige object dankzij de spread-operator)
            const raw = (data.schedules || []).find(s => String(s.id || s._id) === String(selectedEvent.id)) || props;
            
            const eventType = raw.eventType || raw.type || 'college';
            const typeLabels = {
              lesson: 'College',
              college: 'College',
              exam: 'Tentamen',
              resit: 'Hertentamen',
              'bp-so-submission': 'Inleveren BP/SO',
              presentation: 'Presentatie',
              'report-submission': 'Verslag indiening',
              'report-review': 'Verslag bespreking',
              activity: 'Activiteit',
              event: 'Evenement'
            };
            const typeLabel = typeLabels[eventType] || eventType;
            
            const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '-';
            const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '-';
            const description = raw.description;
            
            return (
              <>
                <h3>{selectedEvent.title}</h3>
                <table className="event-details-table">
                  <tbody>
                    <tr><td><strong>Type</strong></td><td><span className={`event-type-badge type-${eventType}`}>{typeLabel}</span></td></tr>
                    <tr><td><strong>Datum</strong></td><td>{fmtDate(selectedEvent.start)}</td></tr>
                    <tr><td><strong>Starttijd</strong></td><td>{fmtTime(selectedEvent.start)}</td></tr>
                    <tr><td><strong>Eindtijd</strong></td><td>{fmtTime(selectedEvent.end)}</td></tr>
                    
                    {/* Nieuw & Verbeterd: Toon vaknaam en vakcode apart indien aanwezig */}
                    {raw.courseName && <tr><td><strong>Vaknaam</strong></td><td>{raw.courseName}</td></tr>}
                    {raw.courseCode && <tr><td><strong>Vakcode</strong></td><td>{raw.courseCode}</td></tr>}
                    
                    {/* Nieuw: Toon cohortnaam */}
                    {raw.cohortName && <tr><td><strong>Cohort</strong></td><td>{raw.cohortName}</td></tr>}
                    
                    {raw.examType && <tr><td><strong>Examentype</strong></td><td>{raw.examType}</td></tr>}
                    <tr><td><strong>Wijze</strong></td><td>{raw.mode === 'online' ? 'Online' : 'Fysiek'}</td></tr>
                    
                    {/* Optionele velden (als ze in je database/backend model zitten worden ze nu automatisch gepakt) */}
                    {raw.teacher && <tr><td><strong>Docent</strong></td><td>{raw.teacher}</td></tr>}
                    {raw.room && <tr><td><strong>Lokaal</strong></td><td>{raw.room}</td></tr>}
                    {raw.location && <tr><td><strong>Locatie</strong></td><td>{raw.location}</td></tr>}
                    
                    {description && <tr><td><strong>Beschrijving</strong></td><td>{description}</td></tr>}
                  </tbody>
                </table>
                
                {raw.mode === 'online' && raw.meetingUrl && (
                  <a
                    className="btn-primary"
                    href={raw.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Meeting bijwonen
                  </a>
                )}
                <button className="btn-secondary" style={{ marginTop: '8px' }} onClick={() => setSelectedEvent(null)}>Sluiten</button>
              </>
            );
          })()}
        </div>
      )}
    </>
  );
};

export default StudentCalendar;